/**
 * The Cross Tab Columns option: which dimension fills a report's `\cross_tab` block, the columns a
 * sort field or MF/UF level produces there, and the fill of the block itself, shared by every report
 * that carries one.
 */
import { ValidationError } from "../../../errors";
import { FloorGroup } from "../../../interfaces/AreaSheet";
import { ReportUseGroup } from "../../../interfaces/EstimateUseGroup";
import { LineItem } from "../../../interfaces/LineItem";
import { OverlineQuantitiesMap } from "../../../interfaces/OverlineTotals";
import { ProcessedEstimate, getOverlineQuantities } from "../../datasources/estimate";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { BLANK_LABEL, ColumnNameToIndexMap, codeLabel } from "./common";
import { writeCostDenominators } from "./costDenominators";
import { FloorGroupColumnInfo, handleFloorGroupsWithDenominators } from "./floorGroups";
import { handleDynamicColumns } from "./print";
import { columnNames, rowNames } from "./rangeNames";
import { UseGroupColumnInfo, handleUseGroupsWithDenominators } from "./useGroups";

const SORT_FIELD_VALUE_PREFIX = "sort_field_";
const MFUF_KEY = /^(mf|uf)(\d+)$/;

/** Stored option value that selects a custom sort field as the cross-tab columns. */
export const crossTabSortFieldOptionValue = (sortFieldId: string): string => `${SORT_FIELD_VALUE_PREFIX}${sortFieldId}`;

export const CROSS_TAB_FLOOR_GROUPS_VALUE = "floor_group";

export type CrossTabDimension =
  | { kind: "use_group" }
  | { kind: "floor_group" }
  | { kind: "sort_field"; sortFieldId: string }
  /** An MF or UF level, stored as the item property it reads, e.g. `mf2`. */
  | { kind: "mfuf"; key: string };

/** An unset option means use groups, which is what the Cross Tab has always shown. */
export const parseCrossTabColumns = (value: unknown): CrossTabDimension => {
  if (typeof value !== "string" || value === "") return { kind: "use_group" };
  if (value === CROSS_TAB_FLOOR_GROUPS_VALUE) return { kind: "floor_group" };
  if (MFUF_KEY.test(value)) return { kind: "mfuf", key: value };
  if (value.startsWith(SORT_FIELD_VALUE_PREFIX)) {
    return { kind: "sort_field", sortFieldId: value.slice(SORT_FIELD_VALUE_PREFIX.length) };
  }
  return { kind: "use_group" };
};

/** The MF/UF levels any of the items carry, MF before UF and shallow before deep. */
export const mfufLevelsOf = (items: LineItem[]): string[] => {
  const levels = new Set<string>();
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (MFUF_KEY.test(key)) levels.add(key);
    }
  }
  return Array.from(levels).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

/** `mf2` reads as "MF2" in the option list. */
export const mfufLabel = (key: string): string =>
  key.replace(MFUF_KEY, (_, prefix, level) => `${prefix.toUpperCase()}${level}`);

/** A code's cost-per cell: where it sits in the block and the overline quantity it divides by. */
export interface CrossTabCostPer {
  /** Offset from the block's first column. */
  offset: number;
  value: number;
  uom: string;
}

export interface CrossTabSortFieldColumn {
  code: string;
  header: string;
  /** Offset of the code's cost cell from the block's first column. */
  total: number;
  /** Present when the code has an overline quantity at the top level of grouping. */
  costPer?: CrossTabCostPer;
}

export interface CrossTabSortField {
  /** Item property holding an item's code for the dimension, e.g. `Program_code` or `mf1_code`. */
  codeKey: string;
  columns: CrossTabSortFieldColumn[];
  /** One header per cell of the block, cost-per cells included, in block order. */
  headers: string[];
}

/** What a report passes to fill its column blocks. */
export interface FillCrossTabBlocksArgs {
  worksheet: Excel.Worksheet;
  processed: ProcessedEstimate;
  /** Row range the blocks' columns span, e.g. `__estimateSOV` or `__lineItems`. */
  dataRowName: string;
  /** Items whose codes become columns when the block shows a sort field or MF/UF level. */
  lineItems: LineItem[];
  /** Markups printed in the `__markups` rows; their codes count only when the sheet has those rows. */
  markups: LineItem[];
  useGroups: ReportUseGroup[];
  floorGroups: FloorGroup[];
  crossTabColumns: CrossTabDimension;
  showCodesColumns: boolean;
  /** Overline quantities already in hand; left out, they load only when the sheet can show cost-per columns. */
  overlineQuantities?: OverlineQuantitiesMap;
  /** SOV sheets style their first data row and use the plain cost-per styles, not the Detail ones. */
  isSOV: boolean;
}

/** Where the filled blocks' columns are, for the row builders. */
export interface CrossTabBlocksResult {
  /** Set when the `\cross_tab` block shows a sort field or MF/UF level. */
  crossTabSortField?: CrossTabSortField;
  /** Absolute column indices of the use-group columns, from whichever block carries them. */
  useGroupColumnIndices?: UseGroupColumnInfo;
  floorGroupColumnIndices?: FloorGroupColumnInfo;
  costDenominatorsRowIndex?: number;
  costPerColumnsRequested: boolean;
}

/** The part of a block fill the row builders need, whichever dimension filled it. */
interface BlockFill {
  crossTabSortField?: CrossTabSortField;
  useGroupColumnIndices?: UseGroupColumnInfo;
  floorGroupColumnIndices?: FloorGroupColumnInfo;
  costDenominatorsRowIndex?: number;
  costPerColumnsRequested: boolean;
}

/** The column an item lands in: its code for the dimension, or the trailing blank column. */
export const crossTabColumnCode = (item: Record<string, unknown>, codeKey: string): string =>
  String(item[codeKey] ?? "") || BLANK_LABEL;

/**
 * The quantity a code carries when `columnKey` is the top level of grouping, as the Sort Fields
 * table reports it. Sort-field overlines are keyed `code - description`; MF/UF ones carry the
 * lookup's description instead of the item's, so they fall back to matching on the code alone.
 */
const crossTabDenominator = (
  overlineQuantities: OverlineQuantitiesMap,
  columnKey: string,
  code: string,
  description: string
): { value: number; uom: string } | undefined => {
  const byCode = overlineQuantities[columnKey];
  if (!byCode) return undefined;
  const entry =
    byCode[`${code} - ${description}`] ?? Object.entries(byCode).find(([key]) => key.split(" - ")[0] === code)?.[1];
  const value = entry?.__quantity__;
  if (!value || value <= 0) return undefined;
  return { value, uom: entry?.__uom__ || "" };
};

const costPerHeader = (uom: string): string => `$/${uom || "unit"}`;

/**
 * One column per code the given items carry under `columnKey` (a sort field's column name or an
 * MF/UF level), in code order, plus a blank column at the end when any item has no code. Only codes
 * present in the estimate become columns, so application-wide fields with hundreds of codes stay
 * readable. An unknown `columnKey` yields no columns. Headers follow `codeLabel`. With
 * `overlineQuantities`, a code that has a top-level quantity also gets a cost-per column after its
 * cost column.
 */
export const resolveCrossTabColumns = (
  columnKey: string | undefined,
  items: LineItem[],
  showCodes: boolean,
  overlineQuantities?: OverlineQuantitiesMap
): CrossTabSortField => {
  if (!columnKey) return { codeKey: "", columns: [], headers: [] };

  const codeKey = `${columnKey}_code`;
  const descKey = `${columnKey}_desc`;
  const descriptionByCode = new Map<string, string>();
  let hasBlank = false;
  for (const item of items) {
    const code = crossTabColumnCode(item, codeKey);
    if (code === BLANK_LABEL) {
      hasBlank = true;
    } else if (!descriptionByCode.get(code)) {
      descriptionByCode.set(code, String(item[descKey] ?? ""));
    }
  }

  const codes = Array.from(descriptionByCode.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  if (hasBlank) codes.push(BLANK_LABEL);

  const headers: string[] = [];
  const columns: CrossTabSortFieldColumn[] = codes.map((code) => {
    const description = descriptionByCode.get(code);
    const column: CrossTabSortFieldColumn = {
      code,
      header: code === BLANK_LABEL ? BLANK_LABEL : codeLabel(code, description, showCodes),
      total: headers.length,
    };
    headers.push(column.header);

    const denominator =
      overlineQuantities && code !== BLANK_LABEL
        ? crossTabDenominator(overlineQuantities, columnKey, code, description ?? "")
        : undefined;
    if (denominator) {
      column.costPer = { offset: headers.length, ...denominator };
      headers.push(costPerHeader(denominator.uom));
    }
    return column;
  });

  return { codeKey, columns, headers };
};

/**
 * Sheets templated before the Cross Tab Columns option carry the column block as `\use_groups`, or
 * `\floor_groups` on customer-built Buildings sheets. Renaming it to `\cross_tab` on the first
 * regeneration makes such a sheet indistinguishable from a fresh one, so every later path, option
 * visibility included, works unchanged. The unset option still means Use Groups, so a renamed
 * Buildings block shows use groups until the user picks Buildings in the dropdown.
 */
const adoptLegacyCrossTabBlock = async (worksheet: Excel.Worksheet): Promise<void> => {
  const crossTabName = `\\${columnNames.crossTab}`;
  const crossTab = worksheet.names.getItemOrNullObject(crossTabName);
  const legacyBlocks = [columnNames.useGroups, columnNames.floorGroups].map((name) =>
    worksheet.names.getItemOrNullObject(`\\${name}`)
  );
  await worksheet.context.sync();
  if (!crossTab.isNullObject) return;

  const legacyBlock = legacyBlocks.find((item) => !item.isNullObject);
  if (!legacyBlock) return;
  // A name left pointing at #REF! after its columns were deleted is skipped, as the old fill did
  const legacyRange = legacyBlock.getRangeOrNullObject();
  await worksheet.context.sync();
  if (legacyRange.isNullObject) return;
  worksheet.names.add(crossTabName, legacyRange);
  legacyBlock.delete();
  await worksheet.context.sync();
};

/**
 * Clears what a prior run left in the `\cross_tab` block. When the new column count matches,
 * `handleDynamicColumns` only rewrites the header, so the previous dimension's denominators in
 * `\__costDenominators` and the cost-per styles on the data, markup, subtotal and grand-total rows
 * would otherwise linger. The first column is always a total column, so its formats are copied
 * across, the same way the resize path does it.
 */
const resetCrossTabCostPerColumns = async (worksheet: Excel.Worksheet) => {
  const crossTabBlock = worksheet.getRange(`\\${columnNames.crossTab}`);
  const usedRange = worksheet.getUsedRange();
  crossTabBlock.load("rowIndex, columnIndex, columnCount");
  usedRange.load("rowIndex, rowCount");
  await worksheet.context.sync();

  if (crossTabBlock.columnCount > 1) {
    const rowCount = usedRange.rowIndex + usedRange.rowCount - crossTabBlock.rowIndex;
    const source = worksheet.getRangeByIndexes(crossTabBlock.rowIndex, crossTabBlock.columnIndex, rowCount, 1);
    worksheet
      .getRangeByIndexes(crossTabBlock.rowIndex, crossTabBlock.columnIndex + 1, rowCount, crossTabBlock.columnCount - 1)
      .copyFrom(source, Excel.RangeCopyType.formats);
  }

  const denominatorsRow = await getNamedRangeFromWorksheet(worksheet, rowNames.costDenominators);
  if (denominatorsRow) {
    denominatorsRow.load("rowIndex");
    await worksheet.context.sync();
    worksheet
      .getRangeByIndexes(denominatorsRow.rowIndex, crossTabBlock.columnIndex, 1, crossTabBlock.columnCount)
      .clear(Excel.ClearApplyTo.contents);
  }
  await worksheet.context.sync();
};

/**
 * Writes the sort-field columns' overline quantities into the cost denominators row and styles
 * their cost-per cells. Returns the row the cost-per formulas divide by, when the sheet has one.
 */
const writeCrossTabCostDenominators = async (
  worksheet: Excel.Worksheet,
  crossTabSortField: CrossTabSortField,
  dataRowName: string,
  isSOV: boolean
): Promise<{ costDenominatorsRowIndex?: number }> => {
  const denominatorsRow = await getNamedRangeFromWorksheet(worksheet, rowNames.costDenominators);
  if (!denominatorsRow) return {};
  const dataRange = await getNamedRangeFromWorksheet(worksheet, dataRowName);
  if (!dataRange) {
    throw new ValidationError("The worksheet does not contain the required named range", { rangeName: dataRowName });
  }
  const markupsRange = await getNamedRangeFromWorksheet(worksheet, rowNames.markups);
  const crossTabBlock = worksheet.getRange(`\\${columnNames.crossTab}`);
  denominatorsRow.load("rowIndex");
  dataRange.load("rowIndex");
  markupsRange?.load("rowIndex");
  crossTabBlock.load("columnIndex");
  await worksheet.context.sync();

  await writeCostDenominators({
    worksheet,
    columns: crossTabSortField.columns.flatMap(({ costPer }) =>
      costPer
        ? [{ columnIndex: crossTabBlock.columnIndex + costPer.offset, value: costPer.value, uom: costPer.uom }]
        : []
    ),
    costDenominatorsRowIndex: denominatorsRow.rowIndex,
    firstRowIndex: dataRange.rowIndex,
    firstMarkupRowIndex: markupsRange?.rowIndex,
    subTotalsRangeNamePrefix: rowNames.subTotals,
    grandTotalRangeName: rowNames.grandTotal,
    applyStyleToFirstRow: isSOV,
    isSOV,
  });
  return { costDenominatorsRowIndex: denominatorsRow.rowIndex };
};

/** The rows a block's cost-per styles reach, and whether its first data row is styled, per report kind. */
const rowBlockOptions = (isSOV: boolean) => ({
  markupsRangeName: rowNames.markups,
  subTotalsRangeNamePrefix: rowNames.subTotals,
  grandTotalRangeName: rowNames.grandTotal,
  applyStyleToFirstRow: isSOV,
});

/** Fills the `\cross_tab` block with the selected dimension; `undefined` when the sheet has no block. */
const fillCrossTabBlock = async ({
  worksheet,
  processed,
  dataRowName,
  lineItems,
  markups,
  useGroups,
  floorGroups,
  crossTabColumns,
  showCodesColumns,
  overlineQuantities,
  isSOV,
}: FillCrossTabBlocksArgs): Promise<BlockFill | undefined> => {
  if (!(await getNamedRangeFromWorksheet(worksheet, `\\${columnNames.crossTab}`))) return undefined;

  const blockOptions = { ...rowBlockOptions(isSOV), columnRangeName: columnNames.crossTab };
  if (crossTabColumns.kind === "use_group") {
    return handleUseGroupsWithDenominators(worksheet, useGroups, dataRowName, isSOV, blockOptions);
  }
  if (crossTabColumns.kind === "floor_group") {
    return handleFloorGroupsWithDenominators(worksheet, floorGroups, dataRowName, isSOV, blockOptions, useGroups);
  }

  const columnKey =
    crossTabColumns.kind === "mfuf"
      ? crossTabColumns.key
      : processed.sortFieldColumnNameMap[crossTabColumns.sortFieldId];
  // Only a sheet with the denominators row can show cost-per columns, so only it pays for the fetch
  const quantities = (await getNamedRangeFromWorksheet(worksheet, rowNames.costDenominators))
    ? (overlineQuantities ?? (await getOverlineQuantities(processed)))
    : undefined;
  // A sheet without markup rows never prints the markups, so uncoded ones would leave an empty blank column
  const items = (await getNamedRangeFromWorksheet(worksheet, rowNames.markups))
    ? [...lineItems, ...markups]
    : lineItems;
  const crossTabSortField = resolveCrossTabColumns(columnKey, items, showCodesColumns, quantities);
  await handleDynamicColumns(
    worksheet,
    columnNames.crossTab,
    crossTabSortField.headers.map((name) => ({ name })),
    { copyFormat: true }
  );
  await resetCrossTabCostPerColumns(worksheet);
  const { costDenominatorsRowIndex } = await writeCrossTabCostDenominators(
    worksheet,
    crossTabSortField,
    dataRowName,
    isSOV
  );
  return {
    crossTabSortField,
    costDenominatorsRowIndex,
    costPerColumnsRequested: costDenominatorsRowIndex !== undefined,
  };
};

/**
 * Fills a report's column blocks: the dedicated `\use_groups` and `\floor_groups` blocks, then
 * the `\cross_tab` block with whatever the Cross Tab Columns option selects. A legacy block is
 * adopted as the cross-tab block first, so it is filled once. When a sheet carries both a dedicated
 * block and a cross-tab block showing the same dimension, the dedicated one gets the rows.
 */
export const fillCrossTabBlocks = async (args: FillCrossTabBlocksArgs): Promise<CrossTabBlocksResult> => {
  const { worksheet, dataRowName, useGroups, floorGroups, isSOV } = args;
  await adoptLegacyCrossTabBlock(worksheet);
  const blockOptions = rowBlockOptions(isSOV);
  const useGroupsFill = await handleUseGroupsWithDenominators(worksheet, useGroups, dataRowName, isSOV, blockOptions);
  const floorGroupsFill = await handleFloorGroupsWithDenominators(
    worksheet,
    floorGroups,
    dataRowName,
    isSOV,
    blockOptions,
    useGroups
  );
  const crossTabFill = await fillCrossTabBlock(args);

  const fills: BlockFill[] = [useGroupsFill, floorGroupsFill, crossTabFill].filter(
    (fill): fill is BlockFill => fill !== undefined
  );
  return {
    crossTabSortField: crossTabFill?.crossTabSortField,
    useGroupColumnIndices: useGroupsFill?.useGroupColumnIndices ?? crossTabFill?.useGroupColumnIndices,
    floorGroupColumnIndices: floorGroupsFill?.floorGroupColumnIndices ?? crossTabFill?.floorGroupColumnIndices,
    costDenominatorsRowIndex: fills.map((fill) => fill.costDenominatorsRowIndex).find((index) => index !== undefined),
    costPerColumnsRequested: fills.some((fill) => fill.costPerColumnsRequested),
  };
};

/**
 * Points the use-group or floor-group column key at the `\cross_tab` block when that is what the
 * block shows, so the row builders, which find each dimension's block by its own key, fill it. A
 * sheet that also has the dedicated block keeps it. The cross-tab key is dropped either way, so only
 * a block showing a sort field is read as one.
 */
export const aliasCrossTabColumn = (columnMap: ColumnNameToIndexMap, crossTabColumns: CrossTabDimension): void => {
  if (columnMap[columnNames.crossTab] === undefined) return;
  if (crossTabColumns.kind !== "use_group" && crossTabColumns.kind !== "floor_group") return;
  const dimensionKey = crossTabColumns.kind === "use_group" ? columnNames.useGroups : columnNames.floorGroups;
  if (columnMap[dimensionKey] === undefined) columnMap[dimensionKey] = columnMap[columnNames.crossTab];
  delete columnMap[columnNames.crossTab];
};
